import { useRef, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { OutlineRectangleButton } from 'components/buttons/OutlineRectangleButton';

const MAX_LOGO_ICON_SIZE_BYTES = 101000; // 100KB
const MAX_IMAGE_SIZE_BYTES = 301000; // 300KB

const ImageUploader = ({ title, value, onChange }) => {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState('');
  const [error, setError] = useState(false);

  const isSmallImageTitle = title === 'Logo' || title === 'Success Icon';
  const maxFileSize = isSmallImageTitle ? MAX_LOGO_ICON_SIZE_BYTES : MAX_IMAGE_SIZE_BYTES;

  useEffect(() => {
    // initialize preview from value (could be a URL string)
    if (!value) {
      setPreview('');
      return;
    }

    if (typeof value === 'string') {
      setPreview(value);
    } else if (value instanceof File) {
      // Convert File -> base64 data URL for consistent handling
      const reader = new FileReader();
      reader.onload = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(value);
    }
  }, [value]);

  const handleSelectFile = event => {
    const file = event.target.files?.[0];

    if (!file) return;

    // Images only
    if (!file.type.startsWith('image/')) {
      return;
    }

    // Size limit (configurable)
    if (file.size >= maxFileSize) {
      setError(true);
      // Clear any existing preview and do not call onChange
      setPreview('');
      onChange?.(null);
      return;
    }

    // clear previous error when file is acceptable
    setError(false);

    // Convert to base64 data URL and use as preview
    const reader = new FileReader();

    reader.onload = () => {
      setPreview(reader.result);
      // Pass the base64 string to the parent component
      onChange?.({
        name: file.name,
        size: file.size,
        type: file.type,
        data: reader.result,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleDelete = e => {
    e.stopPropagation();
    setPreview('');
    setError(false);
    onChange?.(null);
  };

  return (
    <Box>
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleSelectFile} />

      <Box sx={{ display: 'flex' }}>
        <Box>
          {preview && typeof preview === 'string' && preview.trim() !== '' ? (
            <Box
              component="img"
              src={preview}
              alt="Preview"
              onError={() => {
                // If image fails to load, clear preview to show placeholder instead of alt text
                try {
                  setPreview('');
                } catch (err) {}
              }}
              sx={{
                width: '150px',
              }}
            />
          ) : (
            <Box sx={{ textAlign: 'center', color: '#fff' }} />
          )}
        </Box>

        {/* Button */}
        <Box sx={{ marginLeft: preview ? 2 : 0 }}>
          {preview ? (
            <OutlineRectangleButton width="70px" height="32px" onClick={handleDelete}>
              Delete
            </OutlineRectangleButton>
          ) : (
            <OutlineRectangleButton width="70px" height="32px" onClick={handleClick}>
              Browse
            </OutlineRectangleButton>
          )}
        </Box>
      </Box>
      <Box sx={{ marginTop: 1 }}>
        <Typography sx={{ color: error ? 'error.main' : 'text.primary', fontSize: 12 }}>Maximum upload image size: {isSmallImageTitle ? 100 : 300} KB.</Typography>
      </Box>
    </Box>
  );
};

export default ImageUploader;
